import {
  MaintenanceGuide,
  MaintenanceGuideApiResponseData,
  MaintenanceGuideSavePayload,
  UseMaintenanceGuidesOptions,
  UseMaintenanceGuidesReturn,
} from 'src/SharedCustomHooks/UseMaintenanceGuides/types';
import { useEffect, useMemo, useState } from 'react';
import {
  addFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  fetchMaintainanceGuideBySuiteId,
  getMaintenanceGuidesByItemId,
  updateFaqsNotesDocuments,
} from 'src/services/client';
import {
  buildSaveRequestPayload,
  parseGuideFromApiResponse,
  parseGuideFromNewEndpoint,
} from 'src/SharedCustomHooks/UseMaintenanceGuides/helpers';
import { toast } from 'react-toastify';

export const useMaintenanceGuides = (
  itemOrAssetId: string,
  options: UseMaintenanceGuidesOptions
): UseMaintenanceGuidesReturn => {
  const [guides, setGuides] = useState<MaintenanceGuide[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredGuides = useMemo(() => {
    if (!searchTerm.trim()) {
      return guides;
    }

    const term: string = searchTerm.toLowerCase();

    return guides.filter((guide) =>
      guide.title.toLowerCase().includes(term) ||
      guide.body.toLowerCase().includes(term)
    );
  }, [guides, searchTerm])

  const fetchGuidesBySuite = async () => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await fetchMaintainanceGuideBySuiteId(itemOrAssetId);

      if (response.ok) {
        const data: any = await response.json();
        const parsedGuides: MaintenanceGuide[] = Array.isArray(data)
          ? data.map(parseGuideFromApiResponse)
          : [];
        setGuides(parsedGuides);
      } else {
        setError('Failed to fetch maintenance guides');
      }
    } catch (err) {
      setError(
        'An unexpected error occurred while fetching maintenance guides'
      );
      console.error('useMaintenanceGuides fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuidesByItemId = async () => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await getMaintenanceGuidesByItemId(
        itemOrAssetId,
        options.source
      );

      if (response.ok) {
        const apiResponse: any = await response.json();
        const rawGuidesList: MaintenanceGuideApiResponseData[] =
          apiResponse.data || [];

        const parsedGuides: MaintenanceGuide[] = Array.isArray(rawGuidesList)
          ? rawGuidesList.map((guide: MaintenanceGuideApiResponseData) =>
              parseGuideFromNewEndpoint(guide)
            )
          : [];

        setGuides(parsedGuides);
      } else {
        setError('Failed to fetch maintenance guides');
      }
    } catch (err) {
      setError(
        'An unexpected error occurred while fetching maintenance guides'
      );
      console.error('useMaintenanceGuides fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveGuide = async (guide: Partial<MaintenanceGuide>) => {
    try {
      const saveRequestPayload: MaintenanceGuideSavePayload =
        buildSaveRequestPayload(guide, itemOrAssetId, options);

      const response: any = await addFaqsNotesDocuments(saveRequestPayload);

      if (response.ok) {
        const result: any = await response.json();
        const newGuide: MaintenanceGuide = parseGuideFromApiResponse(
          result.data
        );
        const updatedGuides: MaintenanceGuide[] = [...guides, newGuide];

        setGuides(updatedGuides);
        toast.success('ⓘ  Maintenance reminder has been added.', {
          autoClose: 10000,
          position: 'top-center',
        });
      } else {
        toast.error(
          'ⓘ  Maintenance reminder adding failed, please try again.',
          { autoClose: 10000, position: 'top-center' }
        );
      }
    } catch (err) {
      console.error('useMaintenanceGuides save error:', err);
      toast.error('ⓘ  Something went wrong.', {
        autoClose: 10000,
        position: 'top-center',
      });
    }
  };

  const updateGuide = async (guide: MaintenanceGuide) => {
    try {
      const payload = {
        ...buildSaveRequestPayload(guide, itemOrAssetId, options),
        id: guide.id,
        data: JSON.stringify({
          title: guide.title,
          body: guide.body,
          author: guide.author,
          createdAt: guide.createdAt,
          appointment: guide.appointment,
          toggle: guide.toggle,
          repeatType: guide.repeatType,
          repeatEvery: guide.repeatEvery,
          repeatTypeEvery: guide.repeatTypeEvery,
          IsSuiteWarrantyStartDate: guide.IsSuiteWarrantyStartDate,
        }),
      };

      const response: any = await updateFaqsNotesDocuments(payload);

      if (response.ok) {
        const result: any = await response.json();
        const updatedGuide: MaintenanceGuide = parseGuideFromApiResponse(
          result.data
        );
        const updatedGuides: MaintenanceGuide[] = guides.map(
          (g: MaintenanceGuide) => (g.id === guide.id ? updatedGuide : g)
        );
        setGuides(updatedGuides);
        toast.success('ⓘ  Maintenance reminder has been updated.', {
          autoClose: 10000,
          position: 'top-center',
        });
      } else {
        toast.error(
          'ⓘ  Maintenance reminder updating failed, please try again.',
          { autoClose: 10000, position: 'top-center' }
        );
      }
    } catch (err) {
      console.error('useMaintenanceGuides update error:', err);
      toast.error('ⓘ  Something went wrong.', {
        autoClose: 10000,
        position: 'top-center',
      });
    }
  };

  const deleteGuide = async (guide: MaintenanceGuide) => {
    try {
      const response: any = await deleteFaqsNotesDocuments(guide.id);

      if (response.ok) {
        const updatedGuides: MaintenanceGuide[] = guides.filter(
          (g: MaintenanceGuide) => g.id !== guide.id
        );
        setGuides(updatedGuides);
        toast.success('ⓘ  Maintenance reminder has been deleted.', {
          autoClose: 10000,
          position: 'top-center',
        });
      } else {
        toast.error(
          'ⓘ  Maintenance reminder delete failed, please try again.',
          { autoClose: 10000, position: 'top-center' }
        );
      }
    } catch (err) {
      console.error('useMaintenanceGuides delete error:', err);
      toast.error('ⓘ  Something went wrong.', {
        autoClose: 10000,
        position: 'top-center',
      });
    }
  };

  const refetch = async () => {
    if (options.source === 'Suite') {
      await fetchGuidesBySuite();
    } else {
      await fetchGuidesByItemId();
    }
  };

  useEffect(() => {
    if (!itemOrAssetId) {
      setLoading(false);
      return;
    }

    refetch();
  }, [itemOrAssetId, options.source]);

  return {
    guides: filteredGuides,
    allGuides: guides,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    saveGuide,
    updateGuide,
    deleteGuide,
    refetch,
  };
};
